/**
 * Brand-voice error copy.
 *
 * 87 of the app's ~119 toasts surface a string authored by the backend
 * ("Internal server error", raw Axios messages, Cognito exception text). Those
 * are not ours to rewrite from the frontend, and editing the backend repos was
 * explicitly out of scope, so this maps what we recognise onto the voice and
 * gives everything else a plain fallback.
 *
 * Voice rules that apply here: get serious the moment it matters. Errors are
 * plain and exact — no wink, no exclamation marks, no emoji. Where the user's
 * work is safe, say so, because that is the thing they actually want to know.
 */

const PATTERNS = [
  // --- connectivity -------------------------------------------------------
  [/network error|failed to fetch|err_network|timeout|econnaborted/i,
   "Can't reach the server. Your work is safe — try again in a moment."],

  // --- auth ---------------------------------------------------------------
  [/incorrect username or password|notauthorized/i,
   "That email and password don't match."],
  [/user does not exist|usernotfound/i,
   "No account with that email."],
  [/user is not confirmed|usernotconfirmed/i,
   "Confirm your email first. Check your inbox."],
  [/username already exists|usernameexists/i,
   "There's already an account with that email."],
  [/invalid verification code|codemismatch/i,
   "That code doesn't match. Check it and try again."],
  [/expiredcode/i,
   "That code has expired. Send a new one."],
  [/password.*(policy|length|requirement)|invalidpassword/i,
   "That password is too short. Eight characters or more."],
  [/limit exceeded|toomanyrequests|throttl/i,
   "Too many tries. Wait a minute, then go again."],
  [/(session|token).*(expired|invalid)|unauthorized|401/i,
   "You've been signed out. Sign in and pick up where you left off."],

  // --- quota and billing (plain, exact — money is never funny) ------------
  [/quota|usage limit|limit reached/i,
   "You've used this month's sessions. Upgrade to keep going."],
  [/payment|card|stripe|checkout/i,
   "That payment didn't go through. Nothing has been charged."],

  // --- the work itself ----------------------------------------------------
  [/transcri/i,
   "That didn't transcribe. Your audio is safe — try again."],
  [/(generate|generation).*(note|fail)|note.*generat/i,
   "That didn't generate. Your transcript is safe — try again."],
  [/save|session/i,
   "That didn't save. Your work is safe — try again."],
  [/upload|file/i,
   "That upload didn't work. Try again."],
  [/template/i,
   "That didn't save to your templates. Try again."],
];

const FALLBACK = "That didn't work. Your work is safe — try again.";

/**
 * Turn any thrown value into a sentence a tired clinician can act on.
 *
 * @param {unknown} error   an Axios error, an Error, or a raw string
 * @param {string} [fallback] copy for the case nothing matches
 * @returns {string}
 */
export const errorCopy = (error, fallback = FALLBACK) => {
  const raw = [
    error?.response?.data?.error,
    error?.response?.data?.message,
    error?.message,
    typeof error === "string" ? error : null,
  ]
    .filter(Boolean)
    .join(" ");

  if (!raw) return fallback;

  const status = error?.response?.status;
  if (status === 401 || status === 403) {
    return "You've been signed out. Sign in and pick up where you left off.";
  }

  const hit = PATTERNS.find(([pattern]) => pattern.test(raw));
  return hit ? hit[1] : fallback;
};

export default errorCopy;
